export type LastOrderItem = {
    id: number;
    type: 'general' | 'pizza' | 'combo' | 'combo_pizza_item' | string;
    name: string;
    menu_item_id: number | null;
    flavors?: Array<{ 
        name: string; 
        id?: number;
        additionalToppings?: Array<{
            name: string;
            price?: number;
            external_id?: number | null;
            menu_item_id?: number | null;
        }>;
    }>;
    crust?: { name: string; id?: number } | null;
    dough?: { name: string; id?: number; price?: number; menu_item_id?: number | null; external_id?: number | null } | null;
    numberOfFlavors?: string | number;
    comboItems?: LastOrderItem[];
    extras?: Array<{
        id: number;
        name: string;
        price: number;
        quantity: number;
        menu_item_id: number | null;
        external_id?: number | null;
    }>;
};

type Removed = { item: LastOrderItem; motivo: string };

const normalize = (str: string): string =>
    (str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const isOn = (v: any): boolean => v === 1 || v === '1' || v === true;

const lev = (a: string, b: string): number => {
    const m = a.length,
        n = b.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
        }
    }
    return dp[m][n];
};

const jaccardTokens = (a: string, b: string): number => {
    const tok = (s: string) =>
        new Set(
            normalize(s)
                .replace(/[^\w\s]/g, ' ')
                .split(/\s+/)
                .filter(Boolean)
        );
    const A = tok(a),
        B = tok(b);
    const inter = new Set([...A].filter((x) => B.has(x))).size;
    const uni = new Set([...A, ...B]).size || 1;
    return inter / uni;
};

type FlavorIndex = { set: Set<string>; all: string[] };

const buildFlavorIndex = (menu: any): FlavorIndex => {
    const names: string[] = [];

    (menu?.pizzas?.flavorCategories ?? []).forEach((cat: any) => {
        (cat?.flavors ?? []).forEach((f: any) => {
            if (isOn(f?.available)) names.push(f?.name);
        });
    });

    (menu?.pizzas?.flavors ?? []).forEach((f: any) => {
        if (isOn(f?.available)) names.push(f?.name);
    });

    (menu?.pizzas?.categories ?? []).forEach((cat: any) => {
        (cat?.flavors ?? []).forEach((f: any) => {
            if (isOn(f?.available)) names.push(f?.name);
        });
        (cat?.items ?? []).forEach((f: any) => {
            if (isOn(f?.available)) names.push(f?.name);
        });
    });

    (menu?.combos ?? []).forEach((c: any) => {
        (c?.flavors ?? []).forEach((f: any) => {
            if (isOn(f?.available)) names.push(f?.name);
        });
        (c?.allowedFlavors ?? []).forEach((f: any) => names.push(f));
    });

    const clean = names.filter(Boolean);
    return { set: new Set(clean.map((n) => normalize(n))), all: Array.from(new Set(clean)) };
};

const hasFlavor = (idx: FlavorIndex, name: string, logSuggestions = true): boolean => {
    const norm = normalize(name);
    if (idx.set.has(norm)) return true;

    let best: { candidate: string; score: number } | null = null;
    for (const cand of idx.all) {
        const lc = normalize(cand);
        const maxLen = Math.max(lc.length, norm.length) || 1;
        const simLev = 1 - lev(lc, norm) / maxLen;
        const simJac = jaccardTokens(cand, name);
        const score = Math.max(simLev, simJac);
        if (score >= 0.88) return true;
        if (!best || score > best.score) best = { candidate: cand, score };
    }
    if (logSuggestions && best) {
        console.warn(`[Cardápio] Sabor não encontrado: "${name}". Sugerido: "${best.candidate}" (score ${best.score.toFixed(2)})`);
    }
    return false;
};

type FlavorRecord = { id: number; name: string; norm: string };

const buildFlavorIdIndex = (menu: any) => {
    const list: FlavorRecord[] = [];

    (menu?.pizzas?.categories ?? []).forEach((cat: any) => {
        (cat?.flavors ?? []).forEach((f: any) => {
            if (!isOn(f?.available)) return;
            const id = Number(f?.id);
            const name = String(f?.name ?? '');
            if (Number.isFinite(id) && name) list.push({ id, name, norm: normalize(name) });
        });
    });

    (menu?.pizzas?.flavorCategories ?? []).forEach((cat: any) => {
        (cat?.flavors ?? []).forEach((f: any) => {
            if (!isOn(f?.available)) return;
            const id = Number(f?.id);
            const name = String(f?.name ?? '');
            if (Number.isFinite(id) && name) list.push({ id, name, norm: normalize(name) });
        });
    });

    (menu?.pizzas?.flavors ?? []).forEach((f: any) => {
        if (!isOn(f?.available)) return;
        const id = Number(f?.id);
        const name = String(f?.name ?? '');
        if (Number.isFinite(id) && name) list.push({ id, name, norm: normalize(name) });
    });

    (menu?.combos ?? []).forEach((c: any) => {
        (c?.flavors ?? []).forEach((f: any) => {
            if (!isOn(f?.available)) return;
            const id = Number(f?.id);
            const name = String(f?.name ?? '');
            if (Number.isFinite(id) && name) list.push({ id, name, norm: normalize(name) });
        });
    });

    const seen = new Set<string>();
    const recs: FlavorRecord[] = [];
    for (const r of list) {
        const key = `${r.norm}|${r.id}`;
        if (!seen.has(key)) {
            seen.add(key);
            recs.push(r);
        }
    }

    const byNorm = new Map<string, FlavorRecord[]>();
    for (const r of recs) {
        const arr = byNorm.get(r.norm) ?? [];
        arr.push(r);
        byNorm.set(r.norm, arr);
    }
    return { byNorm, all: recs };
};

const findFlavorRecord = (idx: { byNorm: Map<string, FlavorRecord[]>; all: FlavorRecord[] }, name: string, threshold = 0.88): FlavorRecord | null => {
    const norm = normalize(name);
    const exact = idx.byNorm.get(norm);
    if (exact && exact.length) return exact[0];

    let best: { rec: FlavorRecord; score: number } | null = null;
    for (const rec of idx.all) {
        const maxLen = Math.max(rec.norm.length, norm.length) || 1;
        const simLev = 1 - lev(rec.norm, norm) / maxLen;
        const simJac = jaccardTokens(rec.name, name);
        const score = Math.max(simLev, simJac);
        if (score >= threshold) return rec;
        if (!best || score > best.score) best = { rec, score };
    }
    return null;
};

type CrustOption = { id: number; name: string; norm: string; categoryId: number; available: boolean };
type CrustIndex = {
    sizeToCategory: Map<number, number>;
    byCatName: Map<number, Map<string, CrustOption[]>>;
    byCatList: Map<number, CrustOption[]>;
    byNameGlobal: Map<string, CrustOption[]>;
};

const buildCrustIndex = (menu: any): CrustIndex => {
    const sizeToCategory = new Map<number, number>();
    (menu?.pizzas?.sizes ?? []).forEach((s: any) => {
        const sizeId = Number(s?.id);
        if (!Number.isFinite(sizeId)) return;
        
        if (s?.crust != null && s?.crust !== '') {
            const catId = Number(s?.crust);
            if (Number.isFinite(catId) && catId > 0) {
                sizeToCategory.set(sizeId, catId);
            }
        } else {
            const crustCategories = Array.isArray(s?.crustCategories) ? s.crustCategories : [];
            if (crustCategories.length > 0) {
                const firstCatId = Number(crustCategories[0]);
                if (Number.isFinite(firstCatId) && firstCatId > 0) {
                    sizeToCategory.set(sizeId, firstCatId);
                }
            }
        }
    });

    const byCatName = new Map<number, Map<string, CrustOption[]>>();
    const byCatList = new Map<number, CrustOption[]>();
    const byNameGlobal = new Map<string, CrustOption[]>();

    (menu?.pizzas?.extras?.crust ?? []).forEach((cat: any) => {
        const catId = Number(cat?.id);
        if (!Number.isFinite(catId)) return;

        const list: CrustOption[] = [];
        (cat?.options ?? []).forEach((opt: any) => {
            const id = Number(opt?.id);
            const name = String(opt?.name ?? '');
            const available = isOn(opt?.available);
            if (!Number.isFinite(id) || !name) return;
            const rec: CrustOption = { id, name, norm: normalize(name), categoryId: catId, available };
            list.push(rec);

            const g = byNameGlobal.get(rec.norm) ?? [];
            g.push(rec);
            byNameGlobal.set(rec.norm, g);
        });

        byCatList.set(catId, list);
        const mapName = new Map<string, CrustOption[]>();
        list.forEach((rec) => {
            const arr = mapName.get(rec.norm) ?? [];
            arr.push(rec);
            mapName.set(rec.norm, arr);
        });
        byCatName.set(catId, mapName);
    });

    return { sizeToCategory, byCatName, byCatList, byNameGlobal };
};

const findCrustForItem = (idx: CrustIndex, itemSizeId: number | null | undefined, crustName: string, threshold = 0.88): CrustOption | null => {
    if (!crustName) return null;
    const norm = normalize(crustName);

    const catId = itemSizeId != null ? idx.sizeToCategory.get(Number(itemSizeId)) : undefined;
    if (catId != null) {
        const byName = idx.byCatName.get(catId);
        const exact = byName?.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
        if (exact && exact.length) return exact[0];

        const list = idx.byCatList.get(catId) ?? [];
        let best: { rec: CrustOption; score: number } | null = null;
        for (const rec of list) {
            const maxLen = Math.max(rec.norm.length, norm.length) || 1;
            const simLev = 1 - lev(rec.norm, norm) / maxLen;
            const simJac = jaccardTokens(rec.name, crustName);
            const score = Math.max(simLev, simJac);
            const scoreAdj = score + (rec.available ? 0.05 : 0);
            if (!best || scoreAdj > best.score) best = { rec, score: scoreAdj };
        }
        if (best && best.score >= threshold) return best.rec;
    }

    const g = idx.byNameGlobal.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
    if (g && g.length) return g[0];

    return null;
};

type DoughOption = { id: number; name: string; norm: string; categoryId: number; available: boolean };
type DoughIndex = {
    sizeToCategory: Map<number, number>;
    byCatName: Map<number, Map<string, DoughOption[]>>;
    byCatList: Map<number, DoughOption[]>;
    byNameGlobal: Map<string, DoughOption[]>;
};

const buildDoughIndex = (menu: any): DoughIndex => {
    const sizeToCategory = new Map<number, number>();
    (menu?.pizzas?.sizes ?? []).forEach((s: any) => {
        const sizeId = Number(s?.id);
        if (!Number.isFinite(sizeId)) return;
        
        if (s?.dough != null && s?.dough !== '') {
            const catId = Number(s?.dough);
            if (Number.isFinite(catId) && catId > 0) {
                sizeToCategory.set(sizeId, catId);
            }
        } else {
            const doughCategories = Array.isArray(s?.doughCategories) ? s.doughCategories : [];
            if (doughCategories.length > 0) {
                const firstCatId = Number(doughCategories[0]);
                if (Number.isFinite(firstCatId) && firstCatId > 0) {
                    sizeToCategory.set(sizeId, firstCatId);
                }
            }
        }
    });

    const byCatName = new Map<number, Map<string, DoughOption[]>>();
    const byCatList = new Map<number, DoughOption[]>();
    const byNameGlobal = new Map<string, DoughOption[]>();

    (menu?.pizzas?.extras?.dough ?? []).forEach((cat: any) => {
        const catId = Number(cat?.id);
        if (!Number.isFinite(catId)) return;

        const list: DoughOption[] = [];
        (cat?.options ?? []).forEach((opt: any) => {
            const id = Number(opt?.id);
            const name = String(opt?.name ?? '');
            const available = isOn(opt?.available);
            if (!Number.isFinite(id) || !name) return;
            const rec: DoughOption = { id, name, norm: normalize(name), categoryId: catId, available };
            list.push(rec);

            const g = byNameGlobal.get(rec.norm) ?? [];
            g.push(rec);
            byNameGlobal.set(rec.norm, g);
        });

        byCatList.set(catId, list);
        const mapName = new Map<string, DoughOption[]>();
        list.forEach((rec) => {
            const arr = mapName.get(rec.norm) ?? [];
            arr.push(rec);
            mapName.set(rec.norm, arr);
        });
        byCatName.set(catId, mapName);
    });

    return { sizeToCategory, byCatName, byCatList, byNameGlobal };
};

const findDoughForItem = (idx: DoughIndex, itemSizeId: number | null | undefined, doughName: string, threshold = 0.88): DoughOption | null => {
    if (!doughName) return null;
    const norm = normalize(doughName);

    const catId = itemSizeId != null ? idx.sizeToCategory.get(Number(itemSizeId)) : undefined;
    if (catId != null) {
        const byName = idx.byCatName.get(catId);
        const exact = byName?.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
        if (exact && exact.length) return exact[0];

        const list = idx.byCatList.get(catId) ?? [];
        let best: { rec: DoughOption; score: number } | null = null;
        for (const rec of list) {
            const maxLen = Math.max(rec.norm.length, norm.length) || 1;
            const simLev = 1 - lev(rec.norm, norm) / maxLen;
            const simJac = jaccardTokens(rec.name, doughName);
            const score = Math.max(simLev, simJac);
            const scoreAdj = score + (rec.available ? 0.05 : 0);
            if (!best || scoreAdj > best.score) best = { rec, score: scoreAdj };
        }
        if (best && best.score >= threshold) return best.rec;
    }

    const g = idx.byNameGlobal.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
    if (g && g.length) return g[0];

    return null;
};

type AdditionalToppingOption = { id: number; name: string; norm: string; categoryId: number; available: boolean };
type AdditionalToppingIndex = {
    sizeToCategories: Map<number, Set<number>>;
    byCatName: Map<number, Map<string, AdditionalToppingOption[]>>;
    byCatList: Map<number, AdditionalToppingOption[]>;
    byNameGlobal: Map<string, AdditionalToppingOption[]>;
    byId: Map<number, AdditionalToppingOption>;
};

const buildAdditionalToppingIndex = (menu: any): AdditionalToppingIndex => {
    const sizeToCategories = new Map<number, Set<number>>();
    
    (menu?.pizzas?.sizes ?? []).forEach((s: any) => {
        const sizeId = Number(s?.id);
        if (!Number.isFinite(sizeId)) return;
        
        const categories = new Set<number>();
        const catIds = Array.isArray(s?.additionalToppingsCategories) ? s.additionalToppingsCategories : [];
        catIds.forEach((catId: any) => {
            const id = Number(catId);
            if (Number.isFinite(id)) categories.add(id);
        });
        
        if (categories.size > 0) {
            sizeToCategories.set(sizeId, categories);
        }
    });

    const byCatName = new Map<number, Map<string, AdditionalToppingOption[]>>();
    const byCatList = new Map<number, AdditionalToppingOption[]>();
    const byNameGlobal = new Map<string, AdditionalToppingOption[]>();
    const byId = new Map<number, AdditionalToppingOption>();

    (menu?.pizzas?.extras?.additionalToppings ?? []).forEach((cat: any) => {
        const catId = Number(cat?.id);
        if (!Number.isFinite(catId)) return;

        const list: AdditionalToppingOption[] = [];
        (cat?.options ?? []).forEach((opt: any) => {
            const id = Number(opt?.id);
            const name = String(opt?.name ?? '');
            const available = isOn(opt?.available);
            if (!Number.isFinite(id) || !name) return;
            const rec: AdditionalToppingOption = { id, name, norm: normalize(name), categoryId: catId, available };
            list.push(rec);
            byId.set(id, rec);

            const g = byNameGlobal.get(rec.norm) ?? [];
            g.push(rec);
            byNameGlobal.set(rec.norm, g);
        });

        byCatList.set(catId, list);
        const mapName = new Map<string, AdditionalToppingOption[]>();
        list.forEach((rec) => {
            const arr = mapName.get(rec.norm) ?? [];
            arr.push(rec);
            mapName.set(rec.norm, arr);
        });
        byCatName.set(catId, mapName);
    });

    return { sizeToCategories, byCatName, byCatList, byNameGlobal, byId };
};

const findAdditionalToppingForSize = (
    idx: AdditionalToppingIndex,
    sizeId: number | null | undefined,
    toppingName: string,
    toppingId?: number | null,
    threshold = 0.88
): AdditionalToppingOption | null => {
    if (!toppingName) return null;
    const norm = normalize(toppingName);

    if (toppingId != null && Number.isFinite(Number(toppingId))) {
        const byId = idx.byId.get(Number(toppingId));
        if (byId) {
            if (sizeId != null) {
                const categories = idx.sizeToCategories.get(Number(sizeId));
                if (categories && categories.has(byId.categoryId)) {
                    return byId;
                }
            } else {
                return byId;
            }
        }
    }

    if (sizeId != null) {
        const categories = idx.sizeToCategories.get(Number(sizeId));
        if (categories) {
            for (const catId of categories) {
                const byName = idx.byCatName.get(catId);
                const exact = byName?.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
                if (exact && exact.length) return exact[0];

                const list = idx.byCatList.get(catId) ?? [];
                let best: { rec: AdditionalToppingOption; score: number } | null = null;
                for (const rec of list) {
                    const maxLen = Math.max(rec.norm.length, norm.length) || 1;
                    const simLev = 1 - lev(rec.norm, norm) / maxLen;
                    const simJac = jaccardTokens(rec.name, toppingName);
                    const score = Math.max(simLev, simJac);
                    const scoreAdj = score + (rec.available ? 0.05 : 0);
                    if (!best || scoreAdj > best.score) best = { rec, score: scoreAdj };
                }
                if (best && best.score >= threshold) return best.rec;
            }
        }
    }

    const g = idx.byNameGlobal.get(norm)?.sort((a, b) => Number(b.available) - Number(a.available));
    if (g && g.length) return g[0];

    return null;
};

type ExtraOption = {
    id: number;
    name: string;
    norm: string;
    available: boolean;
    hasStock: boolean;
    extraId: number;
};

type ExtraDefinition = {
    id: number;
    name: string;
    type: number;
    qty_max: number | null;
    qty_min: number | null;
    qtyPerItem: number | null;
    required: number | boolean;
    available: boolean;
    productIds: Set<number>;
    options: Map<number, ExtraOption>;
    optionsByName: Map<string, ExtraOption[]>;
};

type ExtraIndex = {
    byId: Map<number, ExtraDefinition>;
    byProductId: Map<number, Set<number>>;
};

const buildExtraIndex = (menu: any): ExtraIndex => {
    const byId = new Map<number, ExtraDefinition>();
    const byProductId = new Map<number, Set<number>>();

    (menu?.extras ?? []).forEach((extra: any) => {
        const extraId = Number(extra?.id);
        if (!Number.isFinite(extraId)) return;

        const productIds = new Set<number>();
        const options = new Map<number, ExtraOption>();
        const optionsByName = new Map<string, ExtraOption[]>();

        (extra?.options ?? []).forEach((opt: any) => {
            const optId = Number(opt?.id);
            const optName = String(opt?.name ?? '');
            if (!Number.isFinite(optId) || !optName) return;

            const available = isOn(opt?.available);
            const hasStock = opt?.hasStock !== false;
            const norm = normalize(optName);

            const option: ExtraOption = {
                id: optId,
                name: optName,
                norm,
                available,
                hasStock,
                extraId
            };

            options.set(optId, option);

            const byNameArr = optionsByName.get(norm) ?? [];
            byNameArr.push(option);
            optionsByName.set(norm, byNameArr);
        });

        (menu?.general ?? []).forEach((cat: any) => {
            (cat?.products ?? []).forEach((p: any) => {
                const productId = Number(p?.id);
                if (!Number.isFinite(productId)) return;

                const productExtras = Array.isArray(p?.extras) ? p.extras : [];
                if (productExtras.includes(extraId)) {
                    productIds.add(productId);

                    let productExtrasSet = byProductId.get(productId);
                    if (!productExtrasSet) {
                        productExtrasSet = new Set();
                        byProductId.set(productId, productExtrasSet);
                    }
                    productExtrasSet.add(extraId);
                }
            });
        });

        (menu?.combos ?? []).forEach((combo: any) => {
            const comboId = Number(combo?.id);
            if (!Number.isFinite(comboId)) return;

            const comboExtras = Array.isArray(combo?.extras) ? combo.extras : [];
            if (comboExtras.includes(extraId)) {
                productIds.add(comboId);

                let comboExtrasSet = byProductId.get(comboId);
                if (!comboExtrasSet) {
                    comboExtrasSet = new Set();
                    byProductId.set(comboId, comboExtrasSet);
                }
                comboExtrasSet.add(extraId);
            }
        });

        const hasAvailableOptions = Array.from(options.values()).some(opt => opt.available && opt.hasStock);
        const extraAvailable = hasAvailableOptions && (extra?.available !== false && extra?.available !== 0);

        const extraType = Number(extra?.type ?? 0);
        const requiredFlag = isOn(extra?.required) || Number(extra?.required ?? 0) > 0;

        const extraDef: ExtraDefinition = {
            id: extraId,
            name: String(extra?.name ?? ''),
            type: extraType,
            qty_max: extra?.qty_max != null ? Number(extra.qty_max) : null,
            qty_min: extra?.qty_min != null ? Number(extra.qty_min) : null,
            qtyPerItem: extra?.qtyPerItem != null ? Number(extra.qtyPerItem) : null,
            // OP-1064: `required` só vale para grupo "único" (type 1). Em grupo múltiplo a
            // obrigatoriedade vem de qty_min (checado nos call sites junto com este flag), e o
            // `required` do banco vem sujo (o NEP grava 1 e não zera ao trocar o tipo) — o que
            // fazia grupo OPCIONAL ser exigido aqui e o pedido ser rejeitado.
            required: extraType === 1 && requiredFlag,
            available: extraAvailable,
            productIds,
            options,
            optionsByName
        };

        byId.set(extraId, extraDef);
    });

    return { byId, byProductId };
};

const findExtraOption = (extraDef: ExtraDefinition, optionName: string, optionId?: number | null, threshold = 0.88): ExtraOption | null => {
    if (optionId != null) {
        const byId = extraDef.options.get(Number(optionId));
        if (byId) return byId;
    }

    const norm = normalize(optionName);
    const exact = extraDef.optionsByName.get(norm);
    if (exact && exact.length) {
        const available = exact.filter(opt => opt.available && opt.hasStock);
        if (available.length) return available[0];
        return exact[0];
    }

    let best: { opt: ExtraOption; score: number } | null = null;
    for (const opt of extraDef.options.values()) {
        const maxLen = Math.max(opt.norm.length, norm.length) || 1;
        const simLev = 1 - lev(opt.norm, norm) / maxLen;
        const simJac = jaccardTokens(opt.name, optionName);
        const score = Math.max(simLev, simJac);
        const scoreAdj = score + (opt.available && opt.hasStock ? 0.05 : 0);
        if (!best || scoreAdj > best.score) best = { opt, score: scoreAdj };
    }

    if (best && best.score >= threshold) return best.opt;
    return null;
};

const validateExtras = (
    item: LastOrderItem,
    extraIndex: ExtraIndex,
    productId: number
): { valid: boolean; errors: string[]; validExtras: typeof item.extras; shouldReject: boolean; rejectionReason?: string } => {
    const errors: string[] = [];
    const itemExtras = item.extras ?? [];
    const validExtras: typeof item.extras = [];
    const missingExtras: string[] = [];

    const productExtraIds = extraIndex.byProductId.get(productId) ?? new Set<number>();

    if (itemExtras.length === 0) {
        const requiredExtras: string[] = [];
        for (const extraId of productExtraIds) {
            const extraDef = extraIndex.byId.get(extraId);
            if (extraDef && extraDef.available) {
                const hasAvailableOption = Array.from(extraDef.options.values()).some(
                    opt => opt.available && opt.hasStock
                );
                if (hasAvailableOption) {
                    const isRequired = extraDef.required;
                    const hasMinQuantity = extraDef.qty_min != null && extraDef.qty_min > 0;
                    
                    if (isRequired || hasMinQuantity) {
                        const minQtyText = hasMinQuantity ? ` (quantidade mínima: ${extraDef.qty_min})` : '';
                        requiredExtras.push(`${extraDef.name}${minQtyText}`);
                    }
                }
            }
        }

        if (requiredExtras.length > 0) {
            return {
                valid: false,
                errors: [`Extras obrigatórios não selecionados: ${requiredExtras.join(', ')}`],
                validExtras: [],
                shouldReject: true,
                rejectionReason: `Extras obrigatórios não selecionados: ${requiredExtras.join(', ')}`
            };
        }

        return { valid: true, errors: [], validExtras: [], shouldReject: false };
    }

    const extrasByExtraId = new Map<number, typeof itemExtras>();
    for (const extra of itemExtras) {
        let extraId: number | null = null;
        let foundOption: ExtraOption | null = null;

        const optionId = extra.menu_item_id ? Number(extra.menu_item_id) : (extra.id ? Number(extra.id) : null);
        
        if (optionId && Number.isFinite(optionId)) {
            for (const [eId, eDef] of extraIndex.byId.entries()) {
                if (eDef.productIds.has(productId)) {
                    const opt = eDef.options.get(optionId);
                    if (opt) {
                        extraId = eId;
                        foundOption = opt;
                        break;
                    }
                }
            }
        }

        if (!extraId) {
            for (const [eId, eDef] of extraIndex.byId.entries()) {
                if (eDef.productIds.has(productId)) {
                    const opt = findExtraOption(eDef, extra.name, optionId);
                    if (opt) {
                        extraId = eId;
                        foundOption = opt;
                        break;
                    }
                }
            }
        }

        if (!extraId || !Number.isFinite(extraId) || !foundOption) {
            missingExtras.push(extra.name);
            errors.push(`Extra "${extra.name}" não encontrado para este produto`);
            continue;
        }

        const arr = extrasByExtraId.get(extraId) ?? [];
        arr.push(extra);
        extrasByExtraId.set(extraId, arr);
    }

    for (const [extraId, extras] of extrasByExtraId.entries()) {
        const extraDef = extraIndex.byId.get(extraId);
        if (!extraDef) {
            errors.push(`Extra com ID ${extraId} não encontrado no cardápio`);
            continue;
        }

        if (!extraDef.productIds.has(productId)) {
            missingExtras.push(extraDef.name);
            errors.push(`Extra "${extraDef.name}" não está disponível para este produto`);
            continue;
        }

        if (!extraDef.available) {
            missingExtras.push(extraDef.name);
            errors.push(`Extra "${extraDef.name}" está pausado/indisponível`);
            continue;
        }

        const validOptions: typeof itemExtras = [];
        let totalQuantity = 0;

        for (const extra of extras) {
            const option = findExtraOption(extraDef, extra.name, extra.id);
            if (!option) {
                errors.push(`Opção "${extra.name}" não encontrada no extra "${extraDef.name}"`);
                continue;
            }

            if (!option.available || !option.hasStock) {
                missingExtras.push(`${extra.name} (${extraDef.name})`);
                errors.push(`Opção "${extra.name}" do extra "${extraDef.name}" está indisponível`);
                continue;
            }

            let qty = Number(extra.quantity ?? 1);
            if (extraDef.qtyPerItem != null && qty > extraDef.qtyPerItem) {
                const originalQty = qty;
                qty = extraDef.qtyPerItem;
                errors.push(`Extra "${extraDef.name}": quantidade do item "${extra.name}" ajustada de ${originalQty} para ${qty} (máximo por item: ${extraDef.qtyPerItem})`);
            }

            const adjustedExtra = qty !== Number(extra.quantity ?? 1) 
                ? { ...extra, quantity: qty }
                : extra;

            validOptions.push(adjustedExtra);
            totalQuantity += qty;
        }

        if (extraDef.type === 1 && validOptions.length > 1) {
            errors.push(`Extra "${extraDef.name}" é único, mantendo apenas a primeira opção válida`);
            validExtras.push(validOptions[0]);
        } else {
            const qtyMin = extraDef.qty_min;
            if (qtyMin != null && totalQuantity < qtyMin) {
                missingExtras.push(`${extraDef.name} (quantidade mínima: ${qtyMin}, selecionado: ${totalQuantity})`);
                errors.push(`Extra "${extraDef.name}": quantidade selecionada (${totalQuantity}) é menor que o mínimo requerido (${qtyMin})`);
                continue;
            }

            if (extraDef.qty_max != null && totalQuantity > extraDef.qty_max) {
                let remainingQty = extraDef.qty_max;
                const adjustedOptions: typeof itemExtras = [];
                
                for (const opt of validOptions) {
                    const qty = Number(opt.quantity ?? 1);
                    if (remainingQty >= qty) {
                        adjustedOptions.push(opt);
                        remainingQty -= qty;
                    } else if (remainingQty > 0) {
                        adjustedOptions.push({ ...opt, quantity: remainingQty });
                        remainingQty = 0;
                    }
                }
                
                validExtras.push(...adjustedOptions);
                errors.push(`Extra "${extraDef.name}": quantidade total ajustada de ${totalQuantity} para ${extraDef.qty_max} (máximo total: ${extraDef.qty_max})`);
            } else {
                validExtras.push(...validOptions);
            }
        }
    }

    const validExtraIds = new Set<number>();
    for (const extra of validExtras) {
        const optionId = extra.menu_item_id ? Number(extra.menu_item_id) : (extra.id ? Number(extra.id) : null);
        if (optionId && Number.isFinite(optionId)) {
            for (const [eId, eDef] of extraIndex.byId.entries()) {
                if (eDef.productIds.has(productId) && eDef.options.has(optionId)) {
                    validExtraIds.add(eId);
                    break;
                }
            }
        }
    }

    const missingRequiredExtras: string[] = [];
    for (const extraId of productExtraIds) {
        const extraDef = extraIndex.byId.get(extraId);
        if (extraDef && extraDef.available) {
            const hasAvailableOption = Array.from(extraDef.options.values()).some(
                opt => opt.available && opt.hasStock
            );
            if (hasAvailableOption) {
                const isRequired = extraDef.required;
                const hasMinQuantity = extraDef.qty_min != null && extraDef.qty_min > 0;
                const isSelected = validExtraIds.has(extraId);
                
                if ((isRequired || hasMinQuantity) && !isSelected) {
                    const minQtyText = hasMinQuantity ? ` (quantidade mínima: ${extraDef.qty_min})` : '';
                    missingRequiredExtras.push(`${extraDef.name}${minQtyText}`);
                }
            }
        }
    }

    if (missingRequiredExtras.length > 0) {
        errors.push(`Extras obrigatórios não selecionados: ${missingRequiredExtras.join(', ')}`);
        return {
            valid: false,
            errors,
            validExtras: [],
            shouldReject: true,
            rejectionReason: `Extras obrigatórios não selecionados: ${missingRequiredExtras.join(', ')}`
        };
    }

    if (missingExtras.length > 0) {
        return {
            valid: false,
            errors,
            validExtras: [],
            shouldReject: true,
            rejectionReason: `Extras indisponíveis: ${missingExtras.join(', ')}`
        };
    }

    return { valid: true, errors, validExtras, shouldReject: false };
};

const formatValidationMessages = (faltantes: string[], contextLabel: string, returnDetailsOnly: boolean): string => {
    if (faltantes.length === 0) return '';
    
    const grupos: { [key: string]: string[] } = {
        borda: [],
        massa: [],
        adicionais: [],
        sabor: [],
        tamanho: [],
        extras: [],
        outros: []
    };
    
    faltantes.forEach(msg => {
        const lowerMsg = msg.toLowerCase();
        if (lowerMsg.includes('borda') || lowerMsg.includes('crust')) {
            grupos.borda.push(msg);
        } else if (lowerMsg.includes('massa') || lowerMsg.includes('dough')) {
            grupos.massa.push(msg);
        } else if (lowerMsg.includes('adicional') || lowerMsg.includes('topping')) {
            grupos.adicionais.push(msg);
        } else if (lowerMsg.includes('sabor') || lowerMsg.includes('flavor')) {
            grupos.sabor.push(msg);
        } else if (lowerMsg.includes('tamanho') || lowerMsg.includes('size')) {
            grupos.tamanho.push(msg);
        } else if (lowerMsg.includes('extra')) {
            grupos.extras.push(msg);
        } else {
            grupos.outros.push(msg);
        }
    });
    
    const partes: string[] = [];
    
    if (grupos.tamanho.length > 0) {
        partes.push(grupos.tamanho.join(', '));
    }
    
    if (grupos.sabor.length > 0) {
        partes.push(grupos.sabor.join(', '));
    }
    
    if (grupos.borda.length > 0) {
        partes.push(grupos.borda.join(', '));
    }
    
    if (grupos.massa.length > 0) {
        partes.push(grupos.massa.join(', '));
    }
    
    if (grupos.adicionais.length > 0) {
        partes.push(grupos.adicionais.join(', '));
    }
    
    if (grupos.extras.length > 0) {
        partes.push(grupos.extras.join(', '));
    }
    
    if (grupos.outros.length > 0) {
        partes.push(grupos.outros.join(', '));
    }
    
    if (returnDetailsOnly) {
        return partes.join(' | ');
    }
    
    let mensagemFormatada: string;
    if (partes.length > 1) {
        mensagemFormatada = partes.map(p => `• ${p}`).join('\n');
    } else {
        mensagemFormatada = partes[0] || '';
    }
    
    return `${contextLabel} inválido:\n${mensagemFormatada}.`;
};

const buildMenuIndexes = (menu: any) => {
    const generalAvailableIds = new Set<number>();
    (menu?.general ?? []).forEach((cat: any) => {
        (cat?.products ?? []).forEach((p: any) => {
            const available = isOn(p?.available) && !isOn(p?.unavailable) && p?.unavailableByTemplate !== true;
            if (available) generalAvailableIds.add(Number(p.id));
        });
    });

    const pizzaSizeIds = new Set<number>();
    (menu?.pizzas?.sizes ?? []).forEach((s: any) => pizzaSizeIds.add(Number(s.id)));
    (menu?.combos ?? []).forEach((c: any) => (c?.pizzaSizes ?? []).forEach((id: any) => pizzaSizeIds.add(Number(id))));

    const comboById = new Map<number, any>();
    (menu?.combos ?? []).forEach((c: any) => {
        const available = isOn(c?.available) && !isOn(c?.unavailable);
        if (available) comboById.set(Number(c?.id), c);
    });

    const flavorIndex = buildFlavorIndex(menu);
    const flavorIdIndex = buildFlavorIdIndex(menu);
    const crustIndex = buildCrustIndex(menu);
    const doughIndex = buildDoughIndex(menu);
    const extraIndex = buildExtraIndex(menu);
    const additionalToppingIndex = buildAdditionalToppingIndex(menu);

    return { generalAvailableIds, pizzaSizeIds, comboById, flavorIndex, flavorIdIndex, crustIndex, doughIndex, extraIndex, additionalToppingIndex };
};

export const validateLastOrderAgainstMenu = (menu: any, lastOrderItems: LastOrderItem[]) => {
    const { generalAvailableIds, pizzaSizeIds, comboById, flavorIndex, flavorIdIndex, crustIndex, doughIndex, extraIndex, additionalToppingIndex } = buildMenuIndexes(menu);

    const validItems: LastOrderItem[] = [];
    const removed: Removed[] = [];

    const validatePizza = (item: LastOrderItem, contextLabel = 'Pizza', returnDetailsOnly = false): Removed | null => {
        const faltantes: string[] = [];

        const sizeOk = item.menu_item_id ? pizzaSizeIds.has(Number(item.menu_item_id)) : false;
        if (!sizeOk) faltantes.push('tamanho da pizza');

        const flavors = item.flavors ?? [];
        const missingFlavors: string[] = [];
        const missingToppings: string[] = [];
        
        if (flavors.length === 0) {
            faltantes.push('sabor não informado');
        } else {
            for (const f of flavors) {
                const ok = hasFlavor(flavorIndex, f?.name ?? '');
                if (!ok) {
                    missingFlavors.push(f?.name ?? '(sem nome)');
                } else {
                    const toppings = f?.additionalToppings ?? [];
                    if (toppings.length > 0) {
                        for (const topping of toppings) {
                            const toppingOpt = findAdditionalToppingForSize(
                                additionalToppingIndex,
                                item.menu_item_id ? Number(item.menu_item_id) : null,
                                topping.name,
                                topping.menu_item_id ? Number(topping.menu_item_id) : null
                            );
                            
                            if (!toppingOpt) {
                                missingToppings.push(`"${topping.name}" no sabor "${f.name}"`);
                            } else if (!toppingOpt.available) {
                                missingToppings.push(`"${topping.name}" no sabor "${f.name}" (indisponível)`);
                            }
                        }
                    }
                }
            }
            if (missingFlavors.length) {
                faltantes.push(`sabor(es) indisponível(is): ${missingFlavors.join(', ')}`);
            }
            if (missingToppings.length) {
                faltantes.push(`Adicionais indisponíveis: ${missingToppings.join(', ')}`);
            }
        }

        const sizeId = item.menu_item_id ? Number(item.menu_item_id) : null;
        
        if (sizeId != null) {
            const hasAdditionalToppings = flavors.some(f => f?.additionalToppings && f.additionalToppings.length > 0);
            if (hasAdditionalToppings) {
                const allowedCategories = additionalToppingIndex.sizeToCategories.get(sizeId);
                if (!allowedCategories || allowedCategories.size === 0) {
                    const currentSize = (menu?.pizzas?.sizes ?? []).find((s: any) => Number(s?.id) === sizeId);
                    if (currentSize) {
                        const hasAdditionalToppingsField = currentSize?.additionalToppings != null && currentSize?.additionalToppings !== '';
                        const hasAdditionalToppingsCategories = Array.isArray(currentSize?.additionalToppingsCategories) && currentSize.additionalToppingsCategories.length > 0;
                        
                        if (!hasAdditionalToppingsField && !hasAdditionalToppingsCategories) {
                            const toppingNames: string[] = [];
                            flavors.forEach(f => {
                                if (f?.additionalToppings && f.additionalToppings.length > 0) {
                                    f.additionalToppings.forEach(t => {
                                        if (t?.name) toppingNames.push(t.name);
                                    });
                                }
                            });
                            if (toppingNames.length > 0) {
                                faltantes.push(`Adicionais não são mais permitidos para este produto: ${toppingNames.join(', ')}`);
                            }
                        }
                    }
                }
            }
        }
        const requiredCrustCategoryId = sizeId != null ? crustIndex.sizeToCategory.get(sizeId) : null;
        
        let crustNotAllowed = false;
        if (item.crust?.name && requiredCrustCategoryId == null && sizeId != null) {
            const currentSize = (menu?.pizzas?.sizes ?? []).find((s: any) => Number(s?.id) === sizeId);
            if (currentSize) {
                const hasCrustField = currentSize?.crust != null && currentSize?.crust !== '';
                const hasCrustCategories = Array.isArray(currentSize?.crustCategories) && currentSize.crustCategories.length > 0;
                
                if (!hasCrustField && !hasCrustCategories) {
                    faltantes.push(`Borda "${item.crust.name}" não é mais permitida para este produto`);
                    crustNotAllowed = true;
                }
            }
        }
        
        if (requiredCrustCategoryId != null) {
            if (!item.crust || !item.crust.name) {
                const crustCategoryOptions = crustIndex.byCatList.get(requiredCrustCategoryId) ?? [];
                const hasAvailableCrust = crustCategoryOptions.some(opt => opt.available);
                if (hasAvailableCrust) {
                    faltantes.push('Borda obrigatória não selecionada');
                }
            } else {
                const opt = findCrustForItem(crustIndex, item.menu_item_id, item.crust.name);
                if (!opt) {
                    faltantes.push(`Borda "${item.crust.name}" não encontrada ou indisponível`);
                } else if (!opt.available) {
                    faltantes.push(`Borda "${item.crust.name}" está pausada/indisponível`);
                } else {
                    item.crust = { ...item.crust, id: Number(opt.id) };
                }
            }
        } else if (item.crust?.name && !crustNotAllowed) {
            const opt = findCrustForItem(crustIndex, item.menu_item_id, item.crust.name);
            if (!opt) {
                faltantes.push(`Borda "${item.crust.name}" não encontrada ou indisponível`);
            } else if (!opt.available) {
                faltantes.push(`Borda "${item.crust.name}" está pausada/indisponível`);
            } else {
                item.crust = { ...item.crust, id: Number(opt.id) };
            }
        }

        const requiredDoughCategoryId = sizeId != null ? doughIndex.sizeToCategory.get(sizeId) : null;
        
        let doughNotAllowed = false;
        if (item.dough?.name && requiredDoughCategoryId == null && sizeId != null) {
            const currentSize = (menu?.pizzas?.sizes ?? []).find((s: any) => Number(s?.id) === sizeId);
            if (currentSize) {
                const hasDoughField = currentSize?.dough != null && currentSize?.dough !== '';
                const hasDoughCategories = Array.isArray(currentSize?.doughCategories) && currentSize.doughCategories.length > 0;
                
                if (!hasDoughField && !hasDoughCategories) {
                    faltantes.push(`Massa "${item.dough.name}" não é mais permitida para este produto`);
                    doughNotAllowed = true;
                }
            }
        }
        
        if (requiredDoughCategoryId != null) {
            if (!item.dough || !item.dough.name) {
                const doughCategoryOptions = doughIndex.byCatList.get(requiredDoughCategoryId) ?? [];
                const hasAvailableDough = doughCategoryOptions.some(opt => opt.available);
                if (hasAvailableDough) {
                    faltantes.push('Massa obrigatória não selecionada');
                }
            } else {
                const opt = findDoughForItem(doughIndex, item.menu_item_id, item.dough.name);
                if (!opt) {
                    faltantes.push(`Massa "${item.dough.name}" não encontrada ou indisponível`);
                } else if (!opt.available) {
                    faltantes.push(`Massa "${item.dough.name}" está pausada/indisponível`);
                } else {
                    item.dough = { ...item.dough, id: Number(opt.id) };
                }
            }
        } else if (item.dough?.name && !doughNotAllowed) {
            const opt = findDoughForItem(doughIndex, item.menu_item_id, item.dough.name);
            if (!opt) {
                faltantes.push(`Massa "${item.dough.name}" não encontrada ou indisponível`);
            } else if (!opt.available) {
                faltantes.push(`Massa "${item.dough.name}" está pausada/indisponível`);
            } else {
                item.dough = { ...item.dough, id: Number(opt.id) };
            }
        }

        if (item.menu_item_id) {
            const extrasValidation = validateExtras(item, extraIndex, Number(item.menu_item_id));
            if (extrasValidation.shouldReject) {
                faltantes.push(extrasValidation.rejectionReason || 'extras indisponíveis');
            } else {
                item.extras = extrasValidation.validExtras;
                if (extrasValidation.errors.length > 0) {
                    console.warn(`[Validação] ${contextLabel} "${item.name}": alguns extras foram ajustados:`, extrasValidation.errors);
                }
            }
        }

        if (faltantes.length) {
            const motivo = formatValidationMessages(faltantes, contextLabel, returnDetailsOnly);
            return { item, motivo };
        }

        if (item.flavors && item.flavors.length) {
            item.flavors = item.flavors.map((f) => {
                const rec = findFlavorRecord(flavorIdIndex, f?.name ?? '');
                return rec ? { ...f, id: Number(rec.id) } : f;
            });
        }

        return null;
    };

    const validateNonPizzaGeneral = (item: LastOrderItem, returnDetailsOnly = false): Removed | null => {
        if (!item.menu_item_id || !generalAvailableIds.has(Number(item.menu_item_id))) {
            const motivo = returnDetailsOnly 
                ? 'produto não existe mais ou está indisponível no cardápio atual'
                : 'Produto não existe mais ou está indisponível no cardápio atual.';
            return { item, motivo };
        }

        const productId = Number(item.menu_item_id);

        const extrasValidation = validateExtras(item, extraIndex, productId);
        if (extrasValidation.shouldReject) {
            const motivo = returnDetailsOnly
                ? extrasValidation.rejectionReason || 'extras indisponíveis'
                : `${item.name}: ${extrasValidation.rejectionReason || 'extras indisponíveis'}.`;
            return { item, motivo };
        }
        
        item.extras = extrasValidation.validExtras;
        if (extrasValidation.errors.length > 0) {
            console.warn(`[Validação] Item "${item.name}": alguns extras foram ajustados:`, extrasValidation.errors);
        }

        return null;
    };

    const validateCombo = (item: LastOrderItem): Removed | null => {
        const comboId = Number(item.menu_item_id);
        if (!comboId || !comboById.has(comboId)) {
            return { item, motivo: 'Combo indisponível: não encontrado no cardápio atual (ou marcado como indisponível).' };
        }

        const faltantes: string[] = [];

        const extrasValidation = validateExtras(item, extraIndex, comboId);
        if (extrasValidation.shouldReject) {
            faltantes.push(extrasValidation.rejectionReason || 'extras indisponíveis');
        } else {
            item.extras = extrasValidation.validExtras;
            if (extrasValidation.errors.length > 0) {
                console.warn(`[Validação] Combo "${item.name}": alguns extras foram ajustados:`, extrasValidation.errors);
            }
        }

        const fails: Removed[] = [];
        for (const sub of item.comboItems ?? []) {
            if (sub.type === 'combo_pizza_item' || sub.type === 'pizza') {
                const res = validatePizza(sub, 'Pizza do combo', true);
                if (res) fails.push(res);
            } else {
                const res = validateNonPizzaGeneral(sub, true);
                if (res) fails.push(res);
            }
        }

        if (fails.length) {
            const detalhes = fails.map((f) => {
                const itemName = f.item.name || 'Item sem nome';
                let motivo = f.motivo.trim();
                if (motivo.endsWith('.')) {
                    motivo = motivo.slice(0, -1);
                }
                return `${itemName}: ${motivo}`;
            });
            
            if (fails.length === 1) {
                const itemName = fails[0].item.name || 'item';
                let motivo = fails[0].motivo.trim();
                if (motivo.endsWith('.')) {
                    motivo = motivo.slice(0, -1);
                }
                faltantes.push(`Item do combo "${itemName}": ${motivo}`);
            } else {
                faltantes.push(`Itens do combo inválidos:\n${detalhes.map(d => `• ${d}`).join('\n')}`);
            }
        }

        if (faltantes.length > 0) {
            const motivo = formatValidationMessages(faltantes, 'Combo', false);
            return { item, motivo };
        }

        return null;
    };

    for (const item of lastOrderItems) {
        let failure: Removed | null = null;
        switch (item.type) {
            case 'pizza':
                failure = validatePizza(item);
                break;
            case 'combo':
                failure = validateCombo(item);
                break;
            case 'combo_pizza_item':
                failure = validatePizza(item, 'Pizza do combo');
                break;
            default:
                failure = validateNonPizzaGeneral(item);
                break;
        }
        if (failure) removed.push(failure);
        else validItems.push(item);
    }

    return { validItems, removed };
};
